import { useEffect, useRef } from 'react';
import styles from './Checkbox.module.css';
import { classList } from '../../helpers/classList';
import { FormLabel } from '../FormLabel';
import { FieldError } from '../FieldError';
import { v4 } from 'uuid';

export interface CheckboxProps {
  id?: string;
  name?: string;
  className?: string;
  labelClassName?: string;
  checked?: boolean;
  /**
   * Partially-selected state, for a header checkbox governing a list of rows.
   * Renders the dash mark and sets the DOM `indeterminate` property so
   * assistive technology reports "mixed" rather than "unchecked".
   */
  indeterminate?: boolean;
  value: string | number | readonly string[];
  text: string;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
}

export const Checkbox = (props: CheckboxProps) => {
  const id = props.id ?? v4();
  const inputRef = useRef<HTMLInputElement>(null);

  // `indeterminate` has no HTML attribute — it can only be set on the element.
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = props.indeterminate ?? false;
    }
  }, [props.indeterminate]);

  return (
    <div
      className={classList(
        styles.checkbox,
        props.disabled ? styles.disabled : undefined,
        props.indeterminate ? styles.indeterminate : undefined,
        props.className,
      )}
    >
      <input
        ref={inputRef}
        type='checkbox'
        id={id}
        name={props.name}
        onChange={props.onChange}
        onBlur={props.onBlur}
        value={props.value}
        checked={props.checked}
        disabled={props.disabled}
      />
      <FormLabel className={props.labelClassName} htmlFor={id} required={props.required}>
        {props.text}
      </FormLabel>
      {props.error && <FieldError>{props.error}</FieldError>}
    </div>
  );
};
